import type { FC } from 'react'

import { LoaderCircle, Table } from '@skytech/ui'
import { Th } from '@skytech/ui/Table/Th'
import { getQueryFilter, getSortedList } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { NoResults } from '@/components/NoResults'
import { useSortParams } from '@/hooks/searchParams'
import { useRenderLimit } from '@/hooks/use-render-limit'
import { ExportToExcel } from '@/modules/admin/components/ExportToExcel'
import salaryDeductionApplicationResources from '@/modules/applications/salary-deduction/resources'

import { SDA_STATUS_ID } from '../constants'
import queries from '../queries'
import type { SalaryDeductionApplication } from '../types'

import { SalaryDeductionListItem } from './SalaryDeductionListItem'

import './index.styles.scss'

const stringMatcher = (application: SalaryDeductionApplication) => [
  application.user,
  application.product,
  application.code,
  application.deputyManager,
  application.manager,
  application.modifiedBy,
]

const SalaryDeductionList: FC<{
  searchFilter: string
  statusFilter: number
}> = ({ searchFilter, statusFilter }) => {
  const translate = useTranslation()

  const { locale } = useAppContext()
  const [{ querySort, querySortBy }, { handleSort }] =
    useSortParams<keyof SalaryDeductionApplication>()

  const { data: salaryDeductionApplicationList = [], isLoading } = useQuery(
    queries.salaryDeductionApplications(),
    salaryDeductionApplicationResources.getList
  )

  const getFilteredList = () => {
    if (statusFilter === SDA_STATUS_ID.ALL && !searchFilter) return salaryDeductionApplicationList
    if (statusFilter === SDA_STATUS_ID.ALL) {
      return salaryDeductionApplicationList.filter(getQueryFilter(searchFilter, stringMatcher))
    }

    return salaryDeductionApplicationList.filter(
      application =>
        application.status === statusFilter &&
        getQueryFilter(searchFilter, stringMatcher)(application)
    )
  }

  const itemsFiltered = getFilteredList()
  const sortedList = getSortedList({ list: itemsFiltered, querySort, querySortBy, locale })
  const { visibleList, ListLoaderComponent } = useRenderLimit(sortedList, 30)

  if (isLoading) return <LoaderCircle />
  if (!visibleList || visibleList.length === 0) return <NoResults />

  const STATUS_OPTIONS = {
    [SDA_STATUS_ID.NEW]: translate('sda_status/NEW'),
    [SDA_STATUS_ID.APPROVED]: translate('sda_status/APPROVED'),
    [SDA_STATUS_ID.DENIED]: translate('sda_status/DENIED'),
  }

  const excelExportData = sortedList.map(item => ({
    employeeName: item.user,
    employeeNumber: item.employeeNo,
    employeePhone: `${item.countryId} ${item.code}`,
    employeeEmail: item.email,
    service: item.product,
    invoiceDate: item.fromPeriod,
    applicationDate: item.applicationDate,
    amount: item.amount,
    coveredAmount: item.sdaAmount,
    status: STATUS_OPTIONS[item.status],
    comment: item.comment,
    manager: item.manager,
    deputyManager: item.deputyManager,
    modifiedBy: item.modifiedBy,
  }))

  return (
    <>
      <ExportToExcel
        data={excelExportData}
        filename={translate('Salary Deduction Applications')}
        options={{
          columnFormats: {
            invoiceDate: 'date_time',
            applicationDate: 'date_time',
            amount: 'amount',
            coveredAmount: 'amount',
          },
        }}
      />
      <Table rowHeight="medium">
        <thead>
          <tr>
            <th className="is-mobile-hidden"> </th>
            <Th id="user" onClick={handleSort}>
              <Translation id="User" />
            </Th>

            <Th id="manager" className="is-mobile-hidden" onClick={handleSort}>
              <Translation id="Manager" />
            </Th>
            <Th id="modifiedBy" className="is-mobile-hidden" onClick={handleSort}>
              <Translation id="Modified By" />
            </Th>

            <Th id="product" onClick={handleSort}>
              <Translation id="Service" />
            </Th>

            <Th id="fromPeriod" className="is-mobile-hidden" onClick={handleSort}>
              <Translation id="Date" />
            </Th>

            <Th id="sdaAmount" className="number" onClick={handleSort}>
              <Translation id="Amount" />
            </Th>

            <Th id="amount" className="number is-mobile-hidden" onClick={handleSort}>
              <Translation id="Total" />
            </Th>
          </tr>
        </thead>

        <tbody>
          {sortedList.map(item => (
            <SalaryDeductionListItem key={item.id} item={item} searchFilter={searchFilter} />
          ))}
        </tbody>
      </Table>

      <ListLoaderComponent />
    </>
  )
}

export default SalaryDeductionList
